import type { components } from './generated';
export type Schema<K extends keyof components['schemas']> = components['schemas'][K];
export type Page<T> = { items: T[]; nextCursor?: string };
let csrf = '';
export class ApiError extends Error {
  constructor(
    public status: number,
    public problem: Schema<'Problem'>,
  ) {
    super(problem.message);
  }
}
export async function api<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const write = method !== 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  let pendingKey = '';
  if (write) {
    headers['Content-Type'] = 'application/json';
    headers['X-CSRF-Token'] = csrf;
    // Persist only an opaque signature and key; never credentials or request bodies.
    const signature = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(`${method}:${path}:${JSON.stringify(body)}`),
    );
    pendingKey = `dream.pending.${Array.from(new Uint8Array(signature), (x) => x.toString(16).padStart(2, '0')).join('')}`;
    headers['Idempotency-Key'] = sessionStorage.getItem(pendingKey) ?? crypto.randomUUID();
    sessionStorage.setItem(pendingKey, headers['Idempotency-Key']);
  }
  let response: Response;
  try {
    response = await fetch(`/api/v1${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      signal: AbortSignal.timeout(30000),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new Error('连接中断。输入已保留，重试会沿用原操作标识。');
  }
  if (!response.ok) {
    if (response.status < 500 && pendingKey) sessionStorage.removeItem(pendingKey);
    const fallback = {
      code: 'HTTP_ERROR',
      message: `请求失败（${response.status}）`,
      requestId: '',
      retryable: false,
    };
    let problem: Schema<'Problem'>;
    try {
      problem = (await response.json()) as Schema<'Problem'>;
    } catch {
      problem = fallback;
    }
    throw new ApiError(response.status, problem);
  }
  if (pendingKey) sessionStorage.removeItem(pendingKey);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
export async function session(): Promise<Schema<'Session'>> {
  const result = await api<Schema<'Session'>>('/session');
  csrf = result.csrfToken;
  return result;
}
export async function login(email: string, password: string) {
  const result = await api<Schema<'Session'>>('/auth/login', 'POST', { email, password });
  csrf = result.csrfToken;
  return result;
}
export const formatTime = (seconds: number) =>
  `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0')}:${Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0')}`;
export const labels: Record<string, string> = {
  story_bible: '故事圣经',
  character_profile: '文字人设',
  character_appearance: '主形象',
  look: '造型',
  character_package: '角色资产包',
  script: '剧本',
  shot_plan: '分镜',
  timeline: '时间线',
  voice_profile: '声线',
  music: '音乐',
  clip: '视频镜头',
  handoff: '下集交接',
  approved: '已定稿',
  draft: '草稿',
  proposed: '待确认',
  provisional: '预制作',
  needs_review: '待复核',
  valid: '依赖有效',
  chat: '聊天伙伴',
  planner: '制作规划',
  writer: '剧情编剧',
  character_designer: '人物设计',
  storyboard_designer: '分镜设计',
  continuity_reviewer: '连续性审查',
  qa_reviewer: '质量审查',
  image: '图片',
  video: '视频',
  CONNECTION_DISABLED: '连接尚未启用',
  MODEL_UNVERIFIED: '能力待验证',
  MODEL_DISABLED: '尚未启用',
  CONNECTION_NOT_CONFIGURED: '待配置连接',
};

export async function uploadAsset<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrf },
    body: form,
    signal: AbortSignal.timeout(240000),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message ?? '素材上传失败');
  return result as T;
}
